import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, FalhaApi, type TemplateResumoApi } from "../api";
import { Layout } from "../componentes/Layout";
import { useSessao } from "../estado/sessao";

const RETULO_STATUS: Record<string, string> = {
  RASCUNHO: "Rascunho",
  PUBLICADO: "Publicado",
  ARQUIVADO: "Arquivado",
};

export function Templates() {
  const { usuario } = useSessao();
  const navegar = useNavigate();
  const [templates, setTemplates] = useState<TemplateResumoApi[] | null>(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  function carregar() {
    api
      .listarTemplates()
      .then(({ templates }) => setTemplates(templates))
      .catch(() => {
        setErro("Falha ao carregar modelos");
        setTemplates([]);
      });
  }

  useEffect(carregar, []);

  return (
    <Layout>
      <main className="container">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div>
            <h2 style={{ marginBottom: 4 }}>Modelos de vistoria</h2>
            <p style={{ margin: 0, color: "var(--texto-suave)" }}>Defina o checklist que será usado nas próximas vistorias.</p>
          </div>
          {usuario?.papel === "ADMIN" && !mostrarFormulario && (
            <button className="botao botao-primario" style={{ marginTop: 0, whiteSpace: "nowrap" }} onClick={() => setMostrarFormulario(true)}>
              + Criar modelo
            </button>
          )}
        </div>

        {erro && <div className="alerta alerta-erro" role="alert" style={{ marginTop: 16 }}>{erro}</div>}

        {usuario?.papel === "ADMIN" && mostrarFormulario && (
          <div className="cartao" style={{ padding: 16, marginTop: 16 }}>
            <FormularioModelo
              aoCancelar={() => setMostrarFormulario(false)}
              aoCriar={(id) => navegar(`/templates/${id}`)}
            />
          </div>
        )}

        {templates === null ? (
          <p style={{ marginTop: 16 }}>Carregando…</p>
        ) : templates.length === 0 ? (
          <p style={{ marginTop: 16, color: "var(--texto-suave)" }}>Nenhum modelo ainda. Crie um modelo vazio ou duplique um já existente.</p>
        ) : (
          <div className="cartao" style={{ padding: 0, marginTop: 16 }}>
            {templates.map((t) => (
              <Link key={t.id} to={`/templates/${t.id}`} className="item-lista">
                <div className="item-lista-eyebrow">📐 TEMPLATE</div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <div>
                    <div className="item-lista-titulo">{t.nome}</div>
                    <div className="item-lista-meta">
                      {t.tipo} · v{t.versao} · {t._count.secoes} seções
                    </div>
                  </div>
                  <span className={`pill ${t.status === "PUBLICADO" ? "pill-verde" : "pill-neutro"}`}>
                    {RETULO_STATUS[t.status]}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </Layout>
  );
}

function slug(texto: string) {
  return texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
}

function FormularioModelo({ aoCancelar, aoCriar }: { aoCancelar: () => void; aoCriar: (id: string) => void }) {
  const [nome, setNome] = useState("");
  const [tipo, setTipo] = useState("");
  const [codigoEditado, setCodigoEditado] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      const { template } = await api.criarTemplate({ nome: nome.trim(), tipo });
      aoCriar(template.id);
    } catch (e) {
      setErro(e instanceof FalhaApi ? e.message : "Não foi possível criar o modelo");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form onSubmit={enviar}>
      <h3 style={{ marginTop: 0, marginBottom: 4 }}>Novo modelo vazio</h3>
      <p style={{ marginTop: 0, color: "var(--texto-suave)", fontSize: 14 }}>Depois de criar, adicione as seções e os itens do seu checklist.</p>
      {erro && <div className="alerta alerta-erro" role="alert">{erro}</div>}
      <label htmlFor="modelo-nome">Nome do modelo</label>
      <input
        id="modelo-nome"
        value={nome}
        onChange={(e) => { const valor = e.target.value; setNome(valor); if (!codigoEditado) setTipo(slug(valor)); }}
        placeholder="Ex.: Vistoria de segurança predial"
        required
        autoFocus
      />
      <label htmlFor="modelo-tipo">Código do modelo</label>
      <input
        id="modelo-tipo"
        value={tipo}
        onChange={(e) => { setCodigoEditado(true); setTipo(slug(e.target.value)); }}
        placeholder="vistoria_seguranca_predial"
        pattern="[a-z0-9_]+"
        required
      />
      <small style={{ display: "block", marginTop: 5, color: "var(--texto-suave)" }}>Usado internamente para identificar o modelo. Não pode repetir outro modelo.</small>
      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <button className="botao botao-primario" type="submit" disabled={salvando || !nome.trim() || !tipo}>{salvando ? "Criando…" : "Criar e adicionar itens"}</button>
        <button className="botao botao-texto" type="button" disabled={salvando} onClick={aoCancelar}>Cancelar</button>
      </div>
    </form>
  );
}
